'use client';

/**
 * Owns a table's column visibility for a page.
 *
 * Seeds from the width: a phone starts with the primary columns, anything
 * wider starts with all of them. It re-seeds when the viewport crosses the
 * breakpoint, but only until the user has managed a column by hand - after
 * that, resizing never overrides what they chose. Locked columns are forced
 * visible on every write.
 *
 * Ported from glp-frontend `components/helpers/data-table/useColumnVisibility.ts`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';

import { useMediaQuery } from '../../hooks/useMediaQuery';
import './column-meta';
import {
  COMPACT_TABLE_QUERY,
  columnKey,
  defaultVisibility,
  withLockedVisible,
} from './columnVisibility';

export interface ColumnVisibilityControls {
  columnVisibility: VisibilityState;
  /** Hand this to DataTable's onColumnVisibilityChange. */
  setColumnVisibility: (next: VisibilityState) => void;
  toggleColumn: (key: string) => void;
  showAll: () => void;
  /** True below Tailwind's sm breakpoint. */
  isCompact: boolean;
}

export function useColumnVisibility<TData>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[],
): ColumnVisibilityControls {
  const isCompact = useMediaQuery(COMPACT_TABLE_QUERY);
  const [columnVisibility, setState] = useState<VisibilityState>(() =>
    defaultVisibility(columns, isCompact),
  );
  const touchedRef = useRef(false);

  useEffect(() => {
    if (touchedRef.current) return;
    setState(defaultVisibility(columns, isCompact));
    // Columns builders are memoised by their callers; re-seeding on the
    // breakpoint is the intent, re-seeding on every column identity is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompact]);

  const setColumnVisibility = useCallback(
    (next: VisibilityState) => {
      touchedRef.current = true;
      setState(withLockedVisible(columns, next));
    },
    [columns],
  );

  const toggleColumn = useCallback(
    (key: string) => {
      touchedRef.current = true;
      setState((prev) => withLockedVisible(columns, { ...prev, [key]: prev[key] === false }));
    },
    [columns],
  );

  const showAll = useCallback(() => {
    touchedRef.current = true;
    const all: VisibilityState = {};
    columns.forEach((column) => {
      const key = columnKey(column);
      if (key) all[key] = true;
    });
    setState(all);
  }, [columns]);

  return { columnVisibility, setColumnVisibility, toggleColumn, showAll, isCompact };
}
